import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { syncPendingGuestIntake } from '../utils/contactFlowSync';

const AuthContext = createContext(null);
const INTAKE_STORAGE_KEY = 'intakeComplete';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(auth?.currentUser ?? null);
  const [authReady, setAuthReady] = useState(auth ? false : true);
  const [intakeComplete, setIntakeCompleteState] = useState(
    localStorage.getItem(INTAKE_STORAGE_KEY) === 'true'
  );
  const [intakeLoading, setIntakeLoading] = useState(auth ? true : false);

  useEffect(() => {
    if (!auth) {
      setAuthReady(true);
      setIntakeLoading(false);
      return undefined;
    }

    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser);
      setAuthReady(true);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    let isActive = true;

    const resolveIntakeState = async () => {
      if (!authReady) {
        return;
      }

      if (!user) {
        if (isActive) {
          setIntakeCompleteState(false);
          setIntakeLoading(false);
        }
        localStorage.removeItem(INTAKE_STORAGE_KEY);
        return;
      }

      if (!db) {
        const storedValue = localStorage.getItem(INTAKE_STORAGE_KEY) === 'true';
        if (isActive) {
          setIntakeCompleteState(storedValue);
          setIntakeLoading(false);
        }
        return;
      }

      setIntakeLoading(true);

      try {
        const syncedPendingGuestData = await syncPendingGuestIntake(user);

        if (syncedPendingGuestData) {
          if (isActive) {
            setIntakeCompleteState(true);
            setIntakeLoading(false);
          }

          localStorage.setItem(INTAKE_STORAGE_KEY, 'true');
          return;
        }

        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.exists() ? userDoc.data() : null;
        const isComplete = Boolean(userData?.profileComplete);

        if (isActive) {
          setIntakeCompleteState(isComplete);
          setIntakeLoading(false);
        }

        if (isComplete) {
          localStorage.setItem(INTAKE_STORAGE_KEY, 'true');
        } else {
          localStorage.removeItem(INTAKE_STORAGE_KEY);
        }
      } catch (error) {
        const fallbackStored = localStorage.getItem(INTAKE_STORAGE_KEY) === 'true';
        if (isActive) {
          setIntakeCompleteState(fallbackStored);
          setIntakeLoading(false);
        }
      }
    };

    if (authReady) {
      setIntakeLoading(true);
    }
    resolveIntakeState();

    return () => {
      isActive = false;
    };
  }, [authReady, user]);

  const setIntakeComplete = (isComplete) => {
    const nextValue = Boolean(isComplete);
    setIntakeCompleteState(nextValue);
    if (nextValue) {
      localStorage.setItem(INTAKE_STORAGE_KEY, 'true');
    } else {
      localStorage.removeItem(INTAKE_STORAGE_KEY);
    }
  };

  const value = useMemo(
    () => ({
      authReady,
      currentUser: user,
      user,
      intakeComplete,
      loading: !authReady || intakeLoading,
      setIntakeComplete,
      login: (email, password) => {
        if (!auth) {
          return Promise.reject(new Error('Firebase Auth is not configured.'));
        }
        return signInWithEmailAndPassword(auth, email, password);
      },
      register: (email, password) => {
        if (!auth) {
          return Promise.reject(new Error('Firebase Auth is not configured.'));
        }
        return createUserWithEmailAndPassword(auth, email, password);
      },
      logout: async () => {
        setIntakeComplete(false);
        if (!auth) {
          return Promise.resolve();
        }
        return signOut(auth);
      },
    }),
    [authReady, user, intakeComplete, intakeLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider.');
  }

  return context;
}
